import cls from "classnames";
import { useEffect, useState } from "react";
import { TransferCalcRoundOptions } from "@/domains/transfer";
import { UserMemberOptionFormData } from "../types";
// components
import { Button, Divider, Form, Input, Select, Tabs } from "antd";
import { ToTopOutlined, VerticalAlignBottomOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";

export const Step2Form: React.FC<{
  data: UserMemberOptionFormData;
  visible: boolean;
  onPrev: () => void;
  onNext: (data: UserMemberOptionFormData) => void;
  onCancel: () => void;
}> = ({ data, visible, onPrev, onNext, onCancel }) => {
  const { t } = useTranslation();

  const [activeTabKey, setActiveTabKey] = useState("deposit");

  const [form] = Form.useForm<UserMemberOptionFormData>();

  const getDepositRateModeText = (value: number) => {
    if (value === 0) return t("domains.Transfer.exchangeRate");
    else if (value === 1) return t("domains.Transfer.addSub");
    return "";
  };

  const [depositRateModeText, setDepositRateModeText] = useState<string>(
    getDepositRateModeText(data.member_deposit_rate_mode)
  );

  const handleDepositRateModeChange = (value: number) => {
    setDepositRateModeText(getDepositRateModeText(value));
  };

  const getDepositChargeFrom = (value: number) => {
    return value;
  };

  const [depositChargeFrom, setDepositChargeFrom] = useState<number>(
    getDepositChargeFrom(data.member_deposit_charge_from)
  );

  const handleDepositChargeFromChange = (value: number) => {
    setDepositChargeFrom(getDepositChargeFrom(value));
  };

  const getDepositChargeMode = (value: number) => {
    return value;
  };

  const [depositChargeMode, setDepositChargeMode] = useState<number>(
    getDepositChargeMode(data.member_deposit_charge_mode)
  );

  const handleDepositChargeModeChange = (value: number) => {
    setDepositChargeMode(getDepositChargeMode(value));
  };

  const getWithdrawRateModeText = (value: number) => {
    if (value === 0) return t("domains.Transfer.exchangeRate");
    else if (value === 1) return t("domains.Transfer.addSub");
    return "";
  };

  const [withdrawRateModeText, setWithdrawRateModeText] = useState<string>(
    getWithdrawRateModeText(data.member_withdraw_rate_mode)
  );

  const handleWithdrawRateModeChange = (value: number) => {
    setWithdrawRateModeText(getWithdrawRateModeText(value));
  };

  const getWithdrawChargeFrom = (value: number) => {
    return value;
  };

  const [withdrawChargeFrom, setWithdrawChargeFrom] = useState<number>(
    getWithdrawChargeFrom(data.member_withdraw_charge_from)
  );

  const handleWithdrawChargeFromChange = (value: number) => {
    setWithdrawChargeFrom(getDepositChargeFrom(value));
  };

  const getWithdrawChargeMode = (value: number) => {
    return value;
  };

  const [withdrawChargeMode, setWithdrawChargeMode] = useState<number>(
    getWithdrawChargeMode(data.member_withdraw_charge_mode)
  );

  const handleWithdrawChargeModeChange = (value: number) => {
    setWithdrawChargeMode(getDepositChargeMode(value));
  };

  useEffect(() => {
    form.resetFields();
    form.setFieldsValue(data);
  }, [data, form]);

  return (
    <Form
      form={form}
      labelCol={{ span: 6 }}
      className={cls(["mt:16", { hidden: !visible }])}
      onFinish={(values) => {
        onNext(values);
      }}
    >
      <Tabs
        className="pl:16"
        activeKey={activeTabKey}
        items={[
          {
            label: t("domains.Tenant.memberDeposit"),
            key: "deposit",
            forceRender: true,
            icon: <VerticalAlignBottomOutlined />,
            children: (
              <>
                <Form.Item
                  label={t("domains.Tenant.memberDepositCallback")}
                  className="mb:0"
                >
                  <div className="flex">
                    <Form.Item
                      label=""
                      colon={false}
                      required={false}
                      className="flex:1"
                      name="member_deposit_callback"
                    >
                      <Input
                        placeholder={t("systems.Placeholder.pleaseInput", {
                          field: t("domains.Tenant.memberDepositCallback"),
                        })}
                      />
                    </Form.Item>
                  </div>
                </Form.Item>

                <Form.Item
                  label={t("domains.Tenant.memberDepositExpired")}
                  className="mb:0"
                >
                  <div className="flex">
                    <Form.Item className="flex:1" name="member_deposit_expired">
                      <Input
                        type="number"
                        placeholder={t("systems.Placeholder.pleaseInput", {
                          field: t("domains.Tenant.memberDepositExpired"),
                        })}
                      />
                    </Form.Item>
                    <div className="h:30 pl:8 flex jc:center ai:center">
                      {t("systems.Extra.minute")}
                    </div>
                  </div>
                </Form.Item>

                <Form.Item
                  label={t("domains.Tenant.memberDepositCurrency")}
                  className="mb:0"
                >
                  <div className="flex">
                    <Form.Item
                      label=""
                      colon={false}
                      required={false}
                      className="flex:1"
                      name="member_deposit_currency"
                    >
                      <Input
                        placeholder={t("systems.Placeholder.pleaseInput", {
                          field: t("domains.Tenant.memberDepositCurrency"),
                        })}
                      />
                    </Form.Item>
                  </div>
                </Form.Item>

                <Form.Item
                  label={t("domains.Tenant.memberDepositRound")}
                  className="mb:0"
                >
                  <div className="flex">
                    <Form.Item
                      label=""
                      colon={false}
                      required={false}
                      className="flex:1"
                      name="member_deposit_round"
                    >
                      <Select>
                        {TransferCalcRoundOptions.map(({ i18nKey, value }) => (
                          <Select.Option value={value} key={value}>
                            {t(i18nKey)}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </div>
                </Form.Item>

                <Form.Item
                  label={t("domains.Tenant.memberDepositRate")}
                  className="mb:0"
                >
                  <div className="flex">
                    <Form.Item
                      label=""
                      colon={false}
                      className="flex:1"
                      name="member_deposit_rate_mode"
                    >
                      <Select onChange={handleDepositRateModeChange}>
                        <Select.Option value={0}>
                          {t("domains.Transfer.fixedExchangeRate")}
                        </Select.Option>
                        <Select.Option value={1}>
                          {t("domains.Transfer.followBito")} USDT-TWD
                        </Select.Option>
                        <Select.Option value={11}>
                          {t("domains.Transfer.followOKX")} USDT-VND
                        </Select.Option>
                      </Select>
                    </Form.Item>

                    <div className="h:30 px:8 flex jc:center ai:center">
                      {depositRateModeText}
                    </div>

                    <Form.Item
                      label=""
                      colon={false}
                      className="flex:1"
                      name="member_deposit_rate_data"
                    >
                      <Input
                        type="number"
                        placeholder={t("systems.Placeholder.pleaseInput", {
                          field: t("domains.Tenant.memberDepositRate"),
                        })}
                      />
                    </Form.Item>
                  </div>
                </Form.Item>

                <Form.Item
                  label={t("domains.Tenant.memberDepositCharge")}
                  className="mb:0"
                >
                  <div className="flex ai:center gap:16">
                    <Form.Item
                      className="flex:1"
                      name="member_deposit_charge_from"
                    >
                      <Select onChange={handleDepositChargeFromChange}>
                        <Select.Option value={0}>
                          {t("systems.Extra.fromTenant")}
                        </Select.Option>
                        <Select.Option value={1}>
                          {t("systems.Extra.fromMember")}
                        </Select.Option>
                      </Select>
                    </Form.Item>
                    {depositChargeFrom === 0 && (
                      <Form.Item
                        className="flex:1"
                        name="member_deposit_charge_time"
                      >
                        <Select>
                          <Select.Option value={0}>
                            {t("systems.Extra.chargeBefore")}
                          </Select.Option>
                          <Select.Option value={1}>
                            {t("systems.Extra.chargeAfter")}
                          </Select.Option>
                        </Select>
                      </Form.Item>
                    )}
                    <Form.Item
                      label=""
                      colon={false}
                      required={false}
                      className="flex:1"
                      name="member_deposit_charge_mode"
                    >
                      <Select onChange={handleDepositChargeModeChange}>
                        <Select.Option value={0}>
                          {t("domains.Transfer.unit")}
                        </Select.Option>
                        <Select.Option value={1}>
                          {t("domains.Transfer.ratio")}
                        </Select.Option>
                        <Select.Option value={2}>
                          {t("domains.Transfer.unitAndRatio")}
                        </Select.Option>
                      </Select>
                    </Form.Item>

                    {depositChargeMode === 0 && (
                      <Form.Item className="mb:0">
                        <div className="flex">
                          <Form.Item
                            className="flex:1"
                            name="member_deposit_charge_data"
                          >
                            <Input
                              type="number"
                              placeholder={t(
                                "systems.Placeholder.pleaseInput",
                                {
                                  field: t(
                                    "domains.Tenant.memberDepositCharge"
                                  ),
                                }
                              )}
                            />
                          </Form.Item>
                          <div className="h:30 pl:8 flex jc:center ai:center">
                            USDT
                          </div>
                        </div>
                      </Form.Item>
                    )}

                    {depositChargeMode === 1 && (
                      <Form.Item className="mb:0">
                        <div className="flex">
                          <Form.Item
                            className="flex:1"
                            name="member_deposit_charge_rate"
                          >
                            <Input
                              type="number"
                              placeholder={t(
                                "systems.Placeholder.pleaseInput",
                                {
                                  field: t(
                                    "domains.Tenant.memberDepositCharge"
                                  ),
                                }
                              )}
                            />
                          </Form.Item>
                          <div className="h:30 pl:8 flex jc:center ai:center">
                            %
                          </div>
                        </div>
                      </Form.Item>
                    )}

                    {depositChargeMode === 2 && (
                      <Form.Item className="mb:0">
                        <div className="flex">
                          <Form.Item
                            className="flex:1"
                            name="member_deposit_charge_data"
                          >
                            <Input
                              type="number"
                              placeholder={t(
                                "systems.Placeholder.pleaseInput",
                                {
                                  field: t(
                                    "domains.Tenant.memberDepositCharge"
                                  ),
                                }
                              )}
                            />
                          </Form.Item>
                          <div className="h:30 px:8 flex jc:center ai:center">
                            USDT +
                          </div>
                          <Form.Item
                            className="flex:1"
                            name="member_deposit_charge_rate"
                          >
                            <Input
                              type="number"
                              placeholder={t(
                                "systems.Placeholder.pleaseInput",
                                {
                                  field: t(
                                    "domains.Tenant.memberDepositCharge"
                                  ),
                                }
                              )}
                            />
                          </Form.Item>
                          <div className="h:30 pl:8 flex jc:center ai:center">
                            %
                          </div>
                        </div>
                      </Form.Item>
                    )}
                  </div>
                </Form.Item>

                <Form.Item
                  label={t("domains.Tenant.memberDepositAmountBoundary")}
                  className="mb:0"
                >
                  <div className="flex">
                    <Form.Item
                      className="flex:1"
                      name="member_deposit_amount_min"
                    >
                      <Input
                        type="number"
                        placeholder={t("systems.Placeholder.pleaseInput", {
                          field: t("domains.Tenant.memberDepositAmountMin"),
                        })}
                      />
                    </Form.Item>

                    <div className="h:30 px:8 flex jc:center ai:center">
                      USDT ~
                    </div>

                    <Form.Item
                      className="flex:1"
                      name="member_deposit_amount_max"
                    >
                      <Input
                        type="number"
                        placeholder={t("systems.Placeholder.pleaseInput", {
                          field: t("domains.Tenant.memberDepositAmountMax"),
                        })}
                      />
                    </Form.Item>

                    <div className="h:30 px:8 flex jc:center ai:center">
                      USDT
                    </div>
                  </div>
                </Form.Item>

                <Form.Item
                  className="mb:0"
                  label={t("domains.Tenant.memberDepositBlockThreshold")}
                >
                  <div className="flex">
                    <div className="h:30 pr:8 flex jc:center ai:center">
                      {t("systems.Extra.continuous")}
                    </div>

                    <Form.Item
                      className="flex:1"
                      name="member_deposit_block_seconds"
                    >
                      <Input type="number" />
                    </Form.Item>

                    <div className="h:30 px:8 flex jc:center ai:center white-space:nowrap">
                      {t("systems.Extra.happenWithinSeconds")}
                    </div>

                    <Form.Item
                      className="flex:1"
                      name="member_deposit_block_times"
                    >
                      <Input type="number" />
                    </Form.Item>

                    <div className="h:30 pl:8 flex jc:center ai:center">
                      {t("systems.Extra.times")}
                    </div>
                  </div>
                </Form.Item>

                <Form.Item
                  label={t("domains.Tenant.memberDepositBlockSpan")}
                  className="mb:0"
                >
                  <div className="flex">
                    <Form.Item
                      className="flex:1"
                      name="member_deposit_block_span"
                    >
                      <Input
                        type="number"
                        placeholder={t("systems.Placeholder.pleaseInput", {
                          field: t("domains.Tenant.memberDepositBlockSpan"),
                        })}
                      />
                    </Form.Item>
                    <div className="h:30 pl:8 flex jc:center ai:center">
                      {t("systems.Extra.minute")}
                    </div>
                  </div>
                </Form.Item>
              </>
            ),
          },
          {
            label: t("domains.Tenant.memberWithdraw"),
            key: "withdraw",
            forceRender: true,
            icon: <ToTopOutlined />,
            children: (
              <>
                <Form.Item
                  label={t("domains.Tenant.memberWithdrawCallback")}
                  className="mb:0"
                >
                  <div className="flex">
                    <Form.Item
                      label=""
                      colon={false}
                      required={false}
                      className="flex:1"
                      name="member_withdraw_callback"
                    >
                      <Input
                        placeholder={t("systems.Placeholder.pleaseInput", {
                          field: t("domains.Tenant.memberWithdrawCallback"),
                        })}
                      />
                    </Form.Item>
                  </div>
                </Form.Item>

                <Form.Item
                  label={t("domains.Tenant.memberWithdrawExpired")}
                  className="mb:0"
                >
                  <div className="flex">
                    <Form.Item
                      className="flex:1"
                      name="member_withdraw_expired"
                    >
                      <Input
                        type="number"
                        placeholder={t("systems.Placeholder.pleaseInput", {
                          field: t("domains.Tenant.memberWithdrawExpired"),
                        })}
                      />
                    </Form.Item>
                    <div className="h:30 pl:8 flex jc:center ai:center">
                      {t("systems.Extra.minute")}
                    </div>
                  </div>
                </Form.Item>

                <Form.Item
                  label={t("domains.Tenant.memberWithdrawCurrency")}
                  className="mb:0"
                >
                  <div className="flex">
                    <Form.Item
                      label=""
                      colon={false}
                      required={false}
                      className="flex:1"
                      name="member_withdraw_currency"
                    >
                      <Input
                        placeholder={t("systems.Placeholder.pleaseInput", {
                          field: t("domains.Tenant.memberWithdrawCurrency"),
                        })}
                      />
                    </Form.Item>
                  </div>
                </Form.Item>

                <Form.Item
                  label={t("domains.Tenant.memberWithdrawRound")}
                  className="mb:0"
                >
                  <div className="flex">
                    <Form.Item
                      label=""
                      colon={false}
                      required={false}
                      className="flex:1"
                      name="member_withdraw_round"
                    >
                      <Select>
                        {TransferCalcRoundOptions.map(({ i18nKey, value }) => (
                          <Select.Option value={value} key={value}>
                            {t(i18nKey)}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </div>
                </Form.Item>

                <Form.Item
                  label={t("domains.Tenant.memberWithdrawRate")}
                  className="mb:0"
                >
                  <div className="flex">
                    <Form.Item
                      label=""
                      colon={false}
                      className="flex:1"
                      name="member_withdraw_rate_mode"
                    >
                      <Select onChange={handleWithdrawRateModeChange}>
                        <Select.Option value={0}>
                          {t("domains.Transfer.fixedExchangeRate")}
                        </Select.Option>
                        <Select.Option value={1}>
                          {t("domains.Transfer.followBito")} USDT-TWD
                        </Select.Option>
                        <Select.Option value={11}>
                          {t("domains.Transfer.followOKX")} USDT-VND
                        </Select.Option>
                      </Select>
                    </Form.Item>

                    <div className="h:30 px:8 flex jc:center ai:center">
                      {withdrawRateModeText}
                    </div>

                    <Form.Item
                      label=""
                      colon={false}
                      className="flex:1"
                      name="member_withdraw_rate_data"
                    >
                      <Input
                        type="number"
                        placeholder={t("systems.Placeholder.pleaseInput", {
                          field: t("domains.Tenant.memberWithdrawRate"),
                        })}
                      />
                    </Form.Item>
                  </div>
                </Form.Item>

                <Form.Item
                  label={t("domains.Tenant.memberWithdrawCharge")}
                  className="mb:0"
                >
                  <div className="flex ai:center gap:16">
                    <Form.Item
                      className="flex:1"
                      name="member_withdraw_charge_from"
                    >
                      <Select onChange={handleWithdrawChargeFromChange}>
                        <Select.Option value={0}>
                          {t("systems.Extra.fromTenant")}
                        </Select.Option>
                        <Select.Option value={1}>
                          {t("systems.Extra.fromMember")}
                        </Select.Option>
                      </Select>
                    </Form.Item>
                    {withdrawChargeFrom === 0 && (
                      <Form.Item
                        className="flex:1"
                        name="member_withdraw_charge_time"
                      >
                        <Select>
                          <Select.Option value={0}>
                            {t("systems.Extra.chargeBefore")}
                          </Select.Option>
                          <Select.Option value={1}>
                            {t("systems.Extra.chargeAfter")}
                          </Select.Option>
                        </Select>
                      </Form.Item>
                    )}
                    <Form.Item
                      label=""
                      colon={false}
                      required={false}
                      className="flex:1"
                      name="member_withdraw_charge_mode"
                    >
                      <Select onChange={handleWithdrawChargeModeChange}>
                        <Select.Option value={0}>
                          {t("domains.Transfer.unit")}
                        </Select.Option>
                        <Select.Option value={1}>
                          {t("domains.Transfer.ratio")}
                        </Select.Option>
                        <Select.Option value={2}>
                          {t("domains.Transfer.unitAndRatio")}
                        </Select.Option>
                      </Select>
                    </Form.Item>

                    {withdrawChargeMode === 0 && (
                      <Form.Item className="mb:0">
                        <div className="flex">
                          <Form.Item
                            className="flex:1"
                            name="member_withdraw_charge_data"
                          >
                            <Input
                              type="number"
                              placeholder={t(
                                "systems.Placeholder.pleaseInput",
                                {
                                  field: t(
                                    "domains.Tenant.memberWithdrawCharge"
                                  ),
                                }
                              )}
                            />
                          </Form.Item>
                          <div className="h:30 pl:8 flex jc:center ai:center">
                            USDT
                          </div>
                        </div>
                      </Form.Item>
                    )}

                    {withdrawChargeMode === 1 && (
                      <Form.Item className="mb:0">
                        <div className="flex">
                          <Form.Item
                            className="flex:1"
                            name="member_withdraw_charge_rate"
                          >
                            <Input
                              type="number"
                              placeholder={t(
                                "systems.Placeholder.pleaseInput",
                                {
                                  field: t(
                                    "domains.Tenant.memberWithdrawCharge"
                                  ),
                                }
                              )}
                            />
                          </Form.Item>
                          <div className="h:30 pl:8 flex jc:center ai:center">
                            %
                          </div>
                        </div>
                      </Form.Item>
                    )}

                    {withdrawChargeMode === 2 && (
                      <Form.Item className="mb:0">
                        <div className="flex">
                          <Form.Item
                            className="flex:1"
                            name="member_withdraw_charge_data"
                          >
                            <Input
                              type="number"
                              placeholder={t(
                                "systems.Placeholder.pleaseInput",
                                {
                                  field: t(
                                    "domains.Tenant.memberWithdrawCharge"
                                  ),
                                }
                              )}
                            />
                          </Form.Item>
                          <div className="h:30 px:8 flex jc:center ai:center">
                            USDT +
                          </div>
                          <Form.Item
                            className="flex:1"
                            name="member_withdraw_charge_rate"
                          >
                            <Input
                              type="number"
                              placeholder={t(
                                "systems.Placeholder.pleaseInput",
                                {
                                  field: t(
                                    "domains.Tenant.memberWithdrawCharge"
                                  ),
                                }
                              )}
                            />
                          </Form.Item>
                          <div className="h:30 pl:8 flex jc:center ai:center">
                            %
                          </div>
                        </div>
                      </Form.Item>
                    )}
                  </div>
                </Form.Item>

                <Form.Item
                  label={t("domains.Tenant.memberWithdrawAmountBoundary")}
                  className="mb:0"
                >
                  <div className="flex">
                    <Form.Item
                      className="flex:1"
                      name="member_withdraw_amount_min"
                    >
                      <Input
                        type="number"
                        placeholder={t("systems.Placeholder.pleaseInput", {
                          field: t("domains.Tenant.memberWithdrawAmountMin"),
                        })}
                      />
                    </Form.Item>

                    <div className="h:30 px:8 flex jc:center ai:center">
                      USDT ~
                    </div>

                    <Form.Item
                      className="flex:1"
                      name="member_withdraw_amount_max"
                    >
                      <Input
                        type="number"
                        placeholder={t("systems.Placeholder.pleaseInput", {
                          field: t("domains.Tenant.memberWithdrawAmountMax"),
                        })}
                      />
                    </Form.Item>

                    <div className="h:30 px:8 flex jc:center ai:center">
                      USDT
                    </div>
                  </div>
                </Form.Item>

                <Form.Item
                  className="mb:0"
                  label={t("domains.Tenant.memberWithdrawBlockThreshold")}
                >
                  <div className="flex">
                    <div className="h:30 pr:8 flex jc:center ai:center">
                      {t("systems.Extra.continuous")}
                    </div>

                    <Form.Item
                      className="flex:1"
                      name="member_withdraw_block_seconds"
                    >
                      <Input type="number" />
                    </Form.Item>

                    <div className="h:30 px:8 flex jc:center ai:center white-space:nowrap">
                      {t("systems.Extra.happenWithinSeconds")}
                    </div>

                    <Form.Item
                      className="flex:1"
                      name="member_withdraw_block_times"
                    >
                      <Input type="number" />
                    </Form.Item>

                    <div className="h:30 pl:8 flex jc:center ai:center">
                      {t("systems.Extra.times")}
                    </div>
                  </div>
                </Form.Item>

                <Form.Item
                  label={t("domains.Tenant.memberWithdrawBlockSpan")}
                  className="mb:0"
                >
                  <div className="flex">
                    <Form.Item
                      className="flex:1"
                      name="member_withdraw_block_span"
                    >
                      <Input
                        type="number"
                        placeholder={t("systems.Placeholder.pleaseInput", {
                          field: t("domains.Tenant.memberWithdrawBlockSpan"),
                        })}
                      />
                    </Form.Item>
                    <div className="h:30 pl:8 flex jc:center ai:center">
                      {t("systems.Extra.minute")}
                    </div>
                  </div>
                </Form.Item>
              </>
            ),
          },
        ]}
        onChange={setActiveTabKey}
      ></Tabs>

      <Divider />

      <Form.Item>
        <div className="flex jc:end gap:8">
          <Button htmlType="button" onClick={onCancel}>
            {t("systems.Action.cancel")}
          </Button>
          <Button htmlType="button" onClick={onPrev}>
            {t("systems.Action.prev")}
          </Button>
          <Button type="primary" htmlType="submit">
            {t("systems.Action.next")}
          </Button>
        </div>
      </Form.Item>
    </Form>
  );
};
